import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bug, Plus, Search, Trash2, Calendar, User, Building2, MessageSquare, Send, Crown, ChevronDown, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted', color: '#6b7280' },
  { value: 'verified', label: 'Verified', color: '#3b82f6' },
  { value: 'in_process', label: 'In Process', color: '#f59e0b' },
  { value: 'fixed', label: 'Fixed', color: '#10b981' },
  { value: 'complete', label: 'Complete', color: '#8b5cf6' }
];

export default function SupportBugsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [bugs, setBugs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [expandedBugs, setExpandedBugs] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [replyContent, setReplyContent] = useState({});
  const [sendingReply, setSendingReply] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: ''
  });

  const isPlatformAdmin = user?.is_platform_admin;

  useEffect(() => {
    fetchData();
  }, [searchQuery, filterCategory, filterStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterCategory && filterCategory !== 'all') params.append('category_id', filterCategory);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      
      const [bugsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/support/bugs?${params}`),
        axios.get(`${API}/support/categories`)
      ]);
      setBugs(bugsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load bug reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/support/bugs`, formData);
      toast.success('Bug report submitted');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit bug report');
    }
  };

  const handleStatusChange = async (bugId, newStatus) => {
    try {
      await axios.put(`${API}/support/bugs/${bugId}/status`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddReply = async (bugId) => {
    const content = replyContent[bugId];
    if (!content?.trim()) return;
    
    setSendingReply(prev => ({ ...prev, [bugId]: true }));
    try {
      await axios.post(`${API}/support/bugs/${bugId}/replies`, {
        content: content.trim()
      });
      setReplyContent(prev => ({ ...prev, [bugId]: '' }));
      toast.success('Reply added');
      fetchData();
    } catch (error) {
      toast.error('Failed to add reply');
    } finally {
      setSendingReply(prev => ({ ...prev, [bugId]: false }));
    }
  };

  const handleDelete = async (bugId) => {
    if (!window.confirm('Are you sure you want to delete this bug report?')) return;
    
    try {
      await axios.delete(`${API}/support/bugs/${bugId}`);
      toast.success('Bug report deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete bug report');
    }
  };

  const toggleBug = async (bugId) => {
    const isExpanding = !expandedBugs[bugId];
    setExpandedBugs(prev => ({
      ...prev,
      [bugId]: isExpanding
    }));
    
    // Fetch full details when expanding
    if (isExpanding) {
      try {
        const response = await axios.get(`${API}/support/bugs/${bugId}`);
        setBugs(prev => prev.map(b => b.id === bugId ? response.data : b));
      } catch (error) {
        console.error('Failed to load bug details');
      }
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', category_id: '' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  const getCategoryColor = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.color || '#6366f1';
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                <Bug className="w-8 h-8 text-red-500" />
                Bug Reports
              </h1>
              <p className="text-muted-foreground mt-1">Report and track bugs</p>
            </div>
            <Button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-red-500 text-white hover:bg-red-600" data-testid="report-bug-btn">
              <Plus className="w-4 h-4 mr-2" />
              Report Bug
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search bug reports..."
                className="pl-10 bg-muted/30"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48 bg-muted/30">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40 bg-muted/30">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map(status => (
                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bugs List */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading...</div>
          ) : bugs.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-12 text-center">
                <Bug className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No bug reports found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {bugs.map(bug => {
                const statusInfo = getStatusInfo(bug.status);
                const isExpanded = expandedBugs[bug.id];
                
                return (
                  <Card key={bug.id} className="bg-card border-border hover:border-red-500/30 transition-colors">
                    <CardContent className="p-0">
                      {/* Bug Header - Clickable to expand */}
                      <button
                        onClick={() => toggleBug(bug.id)}
                        className="w-full flex items-start gap-3 p-4 text-left hover:bg-muted/20 transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <h3 className="font-semibold">{bug.title}</h3>
                            <span 
                              className="px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ 
                                backgroundColor: `${statusInfo.color}20`,
                                color: statusInfo.color
                              }}
                            >
                              {statusInfo.label}
                            </span>
                            {bug.category_name && (
                              <span 
                                className="px-2 py-0.5 rounded-full text-xs font-medium"
                                style={{ 
                                  backgroundColor: `${getCategoryColor(bug.category_id)}20`,
                                  color: getCategoryColor(bug.category_id)
                                }}
                              >
                                {bug.category_name}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {bug.submitted_by_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {bug.submitted_by_company}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(bug.created_at)}
                            </span>
                            {bug.replies?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                {bug.replies.length} replies
                              </span>
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Bug Details - Expandable */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-0 border-t border-border/50 ml-8">
                          {/* Status Change (Admin) */}
                          {isPlatformAdmin && (
                            <div className="flex items-center gap-3 mt-3 mb-3">
                              <Label className="text-sm">Status:</Label>
                              <Select 
                                value={bug.status} 
                                onValueChange={(v) => handleStatusChange(bug.id, v)}
                              >
                                <SelectTrigger className="w-40 bg-muted/30 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_OPTIONS.map(status => (
                                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(bug.id)}
                                className="text-red-400 hover:text-red-300 ml-auto"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          
                          {/* Description */}
                          <div className="bg-muted/30 rounded-lg p-4 mt-3">
                            <p className="whitespace-pre-wrap">{bug.description}</p>
                          </div>
                          
                          {/* Replies Section */}
                          <div className="mt-4 pt-4 border-t border-border/50">
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <MessageSquare className="w-4 h-4" />
                              Discussion ({bug.replies?.length || 0})
                            </h4>
                            
                            {bug.replies?.length > 0 && (
                              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                                {bug.replies.map((reply, idx) => (
                                  <div key={reply.id || idx} className="bg-muted/20 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-1 text-xs">
                                      <span className="font-medium flex items-center gap-1">
                                        {reply.is_platform_admin && <Crown className="w-3 h-3 text-yellow-500" />}
                                        {reply.user_name}
                                      </span>
                                      <span className="text-muted-foreground">({reply.user_company})</span>
                                      <span className="text-muted-foreground">·</span>
                                      <span className="text-muted-foreground">{formatDate(reply.created_at)}</span>
                                    </div>
                                    <p className="text-sm whitespace-pre-wrap">{reply.content}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Add Reply */}
                            <div className="flex gap-2">
                              <textarea
                                value={replyContent[bug.id] || ''}
                                onChange={(e) => setReplyContent(prev => ({ ...prev, [bug.id]: e.target.value }))}
                                placeholder="Add a reply..."
                                className="flex-1 h-16 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-red-500/50 resize-none"
                              />
                            </div>
                            <div className="flex justify-end mt-2">
                              <Button
                                onClick={() => handleAddReply(bug.id)}
                                disabled={!replyContent[bug.id]?.trim() || sendingReply[bug.id]}
                                size="sm"
                                className="bg-red-500 text-white hover:bg-red-600"
                              >
                                <Send className="w-3 h-3 mr-1" />
                                {sendingReply[bug.id] ? 'Sending...' : 'Reply'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Submit Bug Dialog */}
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Bug className="w-5 h-5 text-red-500" />
                  Report a Bug
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Brief description of the bug"
                    className="bg-muted/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Detailed description of the bug, steps to reproduce, expected vs actual behavior..."
                    className="w-full h-32 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-red-500/50 resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category_id || "none"} onValueChange={(v) => setFormData({ ...formData, category_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-muted/30">
                      <SelectValue placeholder="Select category (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Category</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button onClick={handleSubmit} className="bg-red-500 text-white hover:bg-red-600" data-testid="submit-bug-btn">
                  Submit Bug Report
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
